/**
 * TASK REORDERING - COMPLEX POSITION AND BATCH OPERATIONS
 *
 * This file contains React Query mutation hooks for complex task operations:
 * - Task reordering within and across columns
 * - Batch task updates
 * - Position recalculation and rebalancing
 *
 * These operations handle the most complex task state changes.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';
import { taskKeys } from '../taskKeys';
import { TaskData, FormattedTask, ReorderTaskInput, BatchUpdateTasksInput } from '../taskTypes';
import { db } from '../../../lib/offline-db';
import { queueOfflineOperation } from '../../../lib/offline-sync';
import { getConnectionState } from '../../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../../useQueryErrorHandler';
import { invalidateQuery } from '../../../utils/queryInvalidationUtils';
import { TaskItem } from '../../../types/topic';
import { rebalanceTaskPositionsLocally } from './taskHelpers';
import {
  calculateTaskPosition,
  updateTaskPositionsOptimistically,
} from './positionCalculationHelpers';
import {
  validateReorderInput,
  validateTaskExists,
  validateBatchUpdateInput,
  trackBatchAssigneeChanges,
  createTaskUpdateObject,
} from './taskValidationHelpers';

/**
 * Hook for reordering a task
 * Handles both position changes within a column and moving between columns
 */
export function useReorderTask() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update - immediately update UI before server call
    onMutate: async ({ topicId, taskId, newStatus, newIndex }: ReorderTaskInput) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: taskKeys.byTopic(topicId) });

      // Snapshot the previous value for rollback
      const previousTasks = queryClient.getQueryData<FormattedTask[]>(taskKeys.byTopic(topicId));

      // Optimistically update the task list using helper function
      if (previousTasks) {
        queryClient.setQueryData<FormattedTask[]>(taskKeys.byTopic(topicId), old => {
          if (!old) return old;

          return updateTaskPositionsOptimistically(
            old,
            taskId,
            newStatus as TaskItem['status'] | undefined,
            newIndex
          );
        });
      }

      // Return context for rollback
      return { previousTasks };
    },

    mutationFn: async ({ topicId, taskId, newStatus, newIndex }: ReorderTaskInput) => {
      // Validate input parameters
      const validation = validateReorderInput({ taskId, topicId, newIndex, newStatus });
      if (!validation.isValid) {
        throw new Error(`Invalid reorder parameters: ${validation.errors.join(', ')}`);
      }

      logger.store('Reordering task', {
        topicId,
        taskId,
        newStatus,
        newIndex,
        is_online: isOnline,
      });

      try {
        // Get current task data
        let currentTask: TaskData | null = null;

        if (isOnline) {
          const { data } = await supabase.from('tasks').select('*').eq('id', taskId).single();

          currentTask = data as TaskData;
        } else {
          currentTask = (await db.tasks.get(taskId)) as TaskData;
        }

        // Validate task exists and is not deleted
        const taskValidation = validateTaskExists(currentTask, taskId);
        if (!taskValidation.isValid) {
          throw new Error(taskValidation.error);
        }

        // Check if status is changing
        const statusChanged = newStatus && newStatus !== currentTask.status;
        const currentStatus = currentTask.status;

        // Get tasks in the target status for calculating new position
        let tasksInTargetStatus: TaskData[] = [];

        if (isOnline) {
          const { data } = await supabase
            .from('tasks')
            .select('*')
            .eq('topic_id', topicId)
            .eq('status', newStatus || currentTask.status)
            .order('position');

          tasksInTargetStatus = (data || []) as TaskData[];
        } else {
          const tasks = await db.tasks
            .where('topic_id')
            .equals(topicId)
            .and(
              task =>
                task.status === (newStatus || currentTask.status) &&
                !task.isDeleted &&
                task.id !== taskId // Exclude the task being moved
            )
            .toArray();

          tasksInTargetStatus = tasks as TaskData[];
        }

        // Calculate the new position using helper function
        const positionResult = calculateTaskPosition({
          targetIndex: newIndex,
          tasksInColumn: tasksInTargetStatus,
          isCrossColumnMove: statusChanged,
        });

        const targetPosition = positionResult.targetPosition;
        const needsRebalancing = positionResult.needsRebalancing;

        // Build update object using helper
        const baseUpdates: Record<string, unknown> = {
          position: targetPosition,
        };

        // If status is changing, update that too
        if (statusChanged && newStatus) {
          baseUpdates.status = newStatus;
        }

        const updates = createTaskUpdateObject(baseUpdates);

        // If offline, update locally and queue for sync
        if (!isOnline) {
          try {
            // Update task in IndexedDB
            const updatedTask = {
              ...currentTask,
              ...updates,
            };

            await db.tasks.put(updatedTask);

            // Queue for sync when online
            await queueOfflineOperation('tasks', 'update', taskId, updatedTask);

            // Check if positions need rebalancing
            if (needsRebalancing) {
              // Rebalance positions locally
              await rebalanceTaskPositionsLocally(topicId, newStatus || currentTask.status);
            }

            return {
              taskId,
              topicId,
              oldStatus: currentStatus,
              newStatus: newStatus || currentStatus,
              newPosition: targetPosition,
            };
          } catch (offlineError) {
            const error = handleError(offlineError, {
              action: 'toast',
              context: {
                operation: 'reorderTask',
                taskId,
                topicId,
                mode: 'offline',
              },
            });
            throw error;
          }
        }

        // Online flow - update on server
        try {
          // Update the task position and status
          const { data, error } = await supabase
            .from('tasks')
            .update(updates)
            .eq('id', taskId)
            .select()
            .single();

          if (error) throw error;

          // Save to IndexedDB
          try {
            await db.tasks.put(data);
          } catch (storageError) {
            logger.error(`Error updating task in IndexedDB:`, storageError);
          }

          // Check if positions need rebalancing
          if (needsRebalancing) {
            try {
              await supabase.rpc('rebalance_task_positions', {
                p_topic_id: topicId,
                p_status: newStatus || currentStatus,
                p_position_step: 1000,
              });

              // Refetch tasks in this status to get updated positions
              const { data: refreshedTasks } = await supabase
                .from('tasks')
                .select('*')
                .eq('topic_id', topicId)
                .eq('status', newStatus || currentStatus)
                .order('position');

              if (refreshedTasks) {
                // Update IndexedDB with refreshed tasks
                await db.tasks.bulkPut(refreshedTasks);
              }
            } catch (rebalanceError) {
              logger.error('Error rebalancing task positions:', rebalanceError);
              // Continue execution - rebalancing is helpful but not critical
            }
          }

          return {
            taskId,
            topicId,
            oldStatus: currentStatus,
            newStatus: newStatus || currentStatus,
            newPosition: targetPosition,
          };
        } catch (error) {
          const appError = handleError(error, {
            action: 'toast',
            context: { operation: 'reorderTask', taskId, topicId },
          });
          throw appError;
        }
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'reorderTask', taskId, topicId },
        });
        throw appError;
      }
    },

    // On error, rollback the optimistic update
    onError: (error, variables, context) => {
      // Restore the previous task list
      if (context?.previousTasks) {
        queryClient.setQueryData(taskKeys.byTopic(variables.topicId), context.previousTasks);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to move task. The task has been restored to its original position.',
        context: { operation: 'reorderTask_optimistic_rollback', taskId: variables.taskId },
      });
    },

    onSuccess: result => {
      const { taskId, topicId, oldStatus, newStatus } = result;

      // Invalidate the topic's task list to refresh with server positions
      // This ensures we get the actual server state after successful optimistic update
      invalidateQuery(queryClient, taskKeys.byTopic(topicId), {
        refetchType: 'active',
        context: { operation: 'reorderTask', taskId },
      });

      // If status changed, also invalidate status-specific queries
      if (oldStatus !== newStatus) {
        invalidateQuery(queryClient, taskKeys.byStatus(topicId, oldStatus), {
          refetchType: 'active',
          context: { operation: 'reorderTask', taskId },
        });

        invalidateQuery(queryClient, taskKeys.byStatus(topicId, newStatus), {
          refetchType: 'active',
          context: { operation: 'reorderTask', taskId },
        });
      }
    },

    // Always invalidate after settle (success or error) to ensure cache consistency
    onSettled: (data, error, variables) => {
      // Ensure topic cache is fresh regardless of outcome
      invalidateQuery(queryClient, taskKeys.byTopic(variables.topicId), {
        refetchType: 'active',
        context: { operation: 'reorderTask_settled', taskId: variables.taskId },
      });
    },
  });
}

/**
 * Hook for batch updating multiple tasks at once
 * Useful for applying the same change to many tasks
 */
export function useBatchUpdateTasks() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ topicId, taskIds, updates }: BatchUpdateTasksInput) => {
      // Validate input parameters
      const validation = validateBatchUpdateInput({ taskIds, updates, topicId });
      if (!validation.isValid) {
        throw new Error(`Invalid batch update parameters: ${validation.errors.join(', ')}`);
      }

      logger.store('Batch updating tasks', {
        topicId,
        taskCount: taskIds.length,
        updatedFields: validation.affectedFields,
        is_online: isOnline,
      });

      const filteredUpdates = validation.filteredUpdates;

      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current tasks for assignee tracking
          const currentTasks: TaskData[] = [];

          // Update each task in IndexedDB
          for (const taskId of taskIds) {
            const currentTask = await db.tasks.get(taskId);

            // Validate task exists and is not deleted
            const taskValidation = validateTaskExists(currentTask, taskId);
            if (!taskValidation.isValid) {
              logger.warn(`${taskValidation.error}, skipping`);
              continue;
            }

            currentTasks.push(currentTask!);

            // Merge updates with current data
            const updatedTask = {
              ...currentTask,
              ...filteredUpdates,
            };

            // Update in IndexedDB
            await db.tasks.put(updatedTask);

            // Queue for sync when online
            await queueOfflineOperation('tasks', 'update', taskId, updatedTask);
          }

          // Track assignee changes for cache invalidation
          const assigneeChanges = trackBatchAssigneeChanges(
            currentTasks,
            updates.assignedTo as string | null | undefined
          );

          return {
            topicId,
            taskIds,
            updates: filteredUpdates,
            affectedAssignees: Array.from(assigneeChanges),
          };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: {
              operation: 'batchUpdateTasks',
              topicId,
              taskCount: taskIds.length,
              mode: 'offline',
            },
          });
          throw error;
        }
      }

      // Online flow - update on server
      try {
        // Get current assignments for assignee tracking
        let assigneeChanges = new Set<string>();

        if (updates.assignedTo !== undefined) {
          const { data } = await supabase.from('tasks').select('id, assigned_to').in('id', taskIds);

          if (data) {
            // Track assignee changes using helper
            assigneeChanges = trackBatchAssigneeChanges(
              data as TaskData[],
              updates.assignedTo as string | null | undefined
            );
          }
        }

        // Update all tasks in one query
        const { data, error } = await supabase
          .from('tasks')
          .update(filteredUpdates)
          .in('id', taskIds)
          .select();

        if (error) throw error;

        // Update in IndexedDB
        try {
          if (data && data.length > 0) {
            await db.tasks.bulkPut(data);
          }
        } catch (storageError) {
          logger.error(`Error updating tasks in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return {
          topicId,
          taskIds,
          updates: filteredUpdates,
          affectedAssignees: Array.from(assigneeChanges),
        };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'batchUpdateTasks',
            topicId,
            taskCount: taskIds.length,
          },
        });
        throw appError;
      }
    },

    onSuccess: result => {
      const { topicId, taskIds, affectedAssignees } = result;

      // Invalidate the topic's task list
      invalidateQuery(queryClient, taskKeys.byTopic(topicId), {
        refetchType: 'active',
        context: { operation: 'batchUpdateTasks', topicId },
      });

      // Invalidate each individual task
      for (const taskId of taskIds) {
        invalidateQuery(queryClient, taskKeys.detail(taskId), {
          refetchType: 'active',
          context: { operation: 'batchUpdateTasks', taskId },
        });
      }

      // Invalidate assignee-specific queries
      for (const assigneeId of affectedAssignees) {
        invalidateQuery(queryClient, taskKeys.byAssignee(assigneeId), {
          refetchType: 'active',
          context: { operation: 'batchUpdateTasks', assigneeId },
        });

        invalidateQuery(queryClient, taskKeys.dashboard(assigneeId), {
          refetchType: 'active',
          context: { operation: 'batchUpdateTasks', assigneeId },
        });
      }
    },
  });
}
